import api from "./api";
import {
  Bill,
  Patient,
  CreateBillRequest,
  UpdateBillRequest,
  BillSearchFilters,
  BillStats,
  CreatePatientRequest,
} from "../types/Billing";

export interface BillReturnableLine {
  medicine: string;
  name: string;
  batchNumber: string;
  unitPrice: number;
  discount: number;
  gstRate: number;
  soldQty: number;
  onBillQty?: number;
  alreadyReturned: number;
  returnableQty: number;
  taxableAmount?: number;
  lineTotal: number;
}

export interface BillReturnableResponse {
  bill: {
    _id: string;
    billNumber: string;
    billDate?: string;
    customer?: Bill["customer"];
    patient?: Bill["patient"];
    paymentMethod?: string;
    paymentStatus?: string;
    subtotal?: number;
    lineDiscountTotal?: number;
    billDiscount?: number;
    totalTax?: number;
    roundOff?: number;
    amountPaid?: number;
    balance?: number;
      grandTotal?: number;
      version?: number;
      notes?: string;
  };
  lines: BillReturnableLine[];
  priorReturns: Array<{
    _id: string;
    returnNumber: string;
    returnDate?: string;
    grandTotal: number;
    refundAmount?: number;
    deduction?: number;
    netRefundAmount?: number;
    reason?: string;
    refundMethod?: string;
    items?: any[];
    createdAt?: string;
  }>;
}

export const billingService = {
  // Bill operations
  getBills: async (filters?: BillSearchFilters): Promise<Bill[]> => {
    try {
      console.log("billingService.getBills: Starting with filters:", filters);
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            params.append(key, value.toString());
          }
        });
      }
      const url = `/billing${params.toString() ? `?${params.toString()}` : ""}`;
      console.log("billingService.getBills: Making API call to:", url);
      const response = await api.get(url);
      console.log("billingService.getBills: API response:", response.data);
      // Handle nested data structure: {success: true, data: [...], pagination: {...}}
      const billsData = response.data.data || response.data;
      return Array.isArray(billsData) ? billsData : [];
    } catch (error) {
      console.error("Error fetching bills:", error);
      return [];
    }
  },

  getBillById: async (id: string): Promise<Bill> => {
    const response = await api.get(`/billing/${id}`);
    return response.data.data || response.data;
  },

  createBill: async (billData: CreateBillRequest): Promise<Bill> => {
    const response = await api.post("/billing", billData);
    return response.data.data || response.data;
  },

  updateBill: async (
    id: string,
    billData: UpdateBillRequest
  ): Promise<Bill> => {
    const response = await api.put(`/billing/${id}`, billData);
    return response.data.data || response.data;
  },

  getBillReturnable: async (id: string): Promise<BillReturnableResponse> => {
    const response = await api.get(`/billing/${id}/returnable`);
    return response.data.data || response.data;
  },

  createSaleReturn: async (payload: {
    bill: string;
    items: { medicine: string; quantity: number }[];
    reason?: string;
    refundMethod?: string;
    refundAmount?: number;
    deduction?: number;
  }): Promise<{ saleReturn: any; bill: Bill }> => {
    const response = await api.post("/billing/returns", payload);
    const data = response.data.data || response.data;
    // Support older shape that returned only the sale return doc
    if (data?.saleReturn) return data;
    return { saleReturn: data, bill: data?.bill };
  },

  getBillVersions: async (
    id: string
  ): Promise<{
    current: {
      version: number;
      billNumber: string;
      grandTotal: number;
      amountPaid: number;
    };
    versions: Array<{
      _id: string;
      version: number;
      summary?: string;
      reason?: string;
      createdAt: string;
      snapshot: any;
      saleReturn?: {
        returnNumber?: string;
        netRefundAmount?: number;
        refundMethod?: string;
      };
      createdBy?: { name?: string };
    }>;
  }> => {
    const response = await api.get(`/billing/${id}/versions`);
    return response.data.data || response.data;
  },

  deleteBill: async (id: string): Promise<void> => {
    await api.delete(`/billing/${id}`);
  },

  getBillStats: async (): Promise<BillStats> => {
    try {
      console.log("billingService.getBillStats: Starting API call");
      const response = await api.get("/billing/stats");
      console.log(
        "billingService.getBillStats: Raw API response:",
        response.data
      );
      // Handle the nested response structure from the backend
      const stats = response.data.data || response.data;
      console.log("billingService.getBillStats: Processed stats:", stats);
      return stats;
    } catch (error) {
      console.error("Error fetching bill stats:", error);
      return {
        totalBills: 0,
        totalRevenue: 0,
        pendingAmount: 0,
        pendingPayments: 0,
        totalPatients: 0,
        monthlyRevenue: 0,
        monthlyBills: 0,
        overdueAmount: 0,
      };
    }
  },

  generateBillReport: async (
    startDate: string,
    endDate: string
  ): Promise<any> => {
    const response = await api.get("/billing/reports/enterprise", {
      params: { startDate, endDate, type: "overview" },
    });
    return response.data;
  },

  // Patient operations
  getPatients: async (): Promise<Patient[]> => {
    try {
      const response = await api.get("/patients");
      console.log("getPatients API response:", response.data);
      // Handle nested data structure: {success: true, data: [...], pagination: {...}}
      const patientsData = response.data.data || response.data;
      return Array.isArray(patientsData) ? patientsData : [];
    } catch (error) {
      console.error("Error fetching patients:", error);
      return [];
    }
  },

  getPatientById: async (id: string): Promise<Patient> => {
    const response = await api.get(`/patients/${id}`);
    return response.data.data || response.data;
  },

  createPatient: async (
    patientData: CreatePatientRequest
  ): Promise<Patient> => {
    const response = await api.post("/patients", patientData);
    return response.data.data || response.data;
  },

  updatePatient: async (
    id: string,
    patientData: Partial<CreatePatientRequest>
  ): Promise<Patient> => {
    const response = await api.put(`/patients/${id}`, patientData);
    return response.data.data || response.data;
  },

  deletePatient: async (id: string): Promise<void> => {
    await api.delete(`/patients/${id}`);
  },

  searchPatients: async (query: string): Promise<Patient[]> => {
    const response = await api.get(
      `/patients/search?query=${encodeURIComponent(query)}`
    );
    return response.data;
  },
};

export default billingService;
